import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar as NavbarComponent } from '../components/Navbar';
import { InvoiceCard } from '../components/InvoiceCard';
import { DemoBanner } from '../components/DemoBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useWeb3 } from '../contexts/Web3Context';
import { Loader2, Search, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getEscrowContract } from '../utils/contracts';
import { ethers } from 'ethers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Marketplace = () => {
  const navigate = useNavigate();
  const { account, signer, isConnected } = useWeb3();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [financing, setFinancing] = useState(false);
  const [contractAddresses, setContractAddresses] = useState(null);

  const [filters, setFilters] = useState({
    riskScore: 'all',
    minAmount: '',
    maxAmount: '',
    search: '',
  });

  useEffect(() => {
    fetchInvoices();
    fetchContractAddresses();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [invoices, filters]);

  const fetchContractAddresses = async () => {
    try {
      const response = await axios.get(`${API}/config/contracts`);
      setContractAddresses(response.data);
    } catch (error) {
      console.error('Error fetching contract addresses:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices?status=LISTED`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...invoices];

    if (filters.riskScore !== 'all') {
      const [min, max] = filters.riskScore.split('-').map(Number);
      filtered = filtered.filter(
        (inv) => inv.risk_score >= min && inv.risk_score <= max
      );
    }

    if (filters.minAmount) {
      filtered = filtered.filter((inv) => inv.amount >= parseFloat(filters.minAmount));
    }

    if (filters.maxAmount) {
      filtered = filtered.filter((inv) => inv.amount <= parseFloat(filters.maxAmount));
    }

    if (filters.search) {
      filtered = filtered.filter(
        (inv) =>
          inv.company_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
          inv.invoice_number?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
  };

  const handleFinance = async (invoice) => {
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!contractAddresses?.escrow) {
      toast.error('Contract addresses not configured. Please deploy contracts first.');
      return;
    }

    try {
      setFinancing(true);

      const escrowContract = getEscrowContract(signer, contractAddresses.escrow);
      const amountInWei = ethers.parseEther(invoice.amount.toString());

      toast.info('Waiting for transaction confirmation...');

      const tx = await escrowContract.acceptFinancing(invoice.token_id, {
        value: amountInWei,
      });

      toast.info('Transaction submitted. Waiting for confirmation...');
      const receipt = await tx.wait();

      await axios.put(`${API}/invoices/${invoice.id}`, {
        status: 'FUNDED',
      });

      await axios.post(`${API}/transactions`, {
        invoice_id: invoice.token_id.toString(),
        transaction_hash: receipt.hash,
        transaction_type: 'FINANCE',
        from_address: account.toLowerCase(),
        to_address: invoice.msme_address,
        amount: invoice.amount,
      });

      toast.success('Invoice financed successfully!');
      fetchInvoices();
    } catch (error) {
      console.error('Error financing invoice:', error);
      toast.error(error.message || 'Failed to finance invoice');
    } finally {
      setFinancing(false);
    }
  };

  const handleViewDetails = (invoice) => {
    navigate(`/invoice/${invoice.token_id || invoice.id}`);
  };

  return (
    <div className="App min-h-screen">
      <NavbarComponent />
      
      <div className="container mx-auto px-6 py-12">
        <DemoBanner />
        
        <div className="mb-8">
          <h1 data-testid="marketplace-title" className="text-4xl font-bold mb-2 text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Invoice Marketplace
          </h1>
          <p className="text-slate-600">Browse and finance verified business invoices</p>
        </div>

        <div data-testid="filters-section" className="professional-card p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <SlidersHorizontal className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Filters
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Company or invoice #"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Risk Score</label>
              <Select
                value={filters.riskScore}
                onValueChange={(value) => setFilters({ ...filters, riskScore: value })}
              >
                <SelectTrigger data-testid="risk-score-filter" className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600">
                  <SelectValue placeholder="All risks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Risks</SelectItem>
                  <SelectItem value="1-3">Low Risk (1-3)</SelectItem>
                  <SelectItem value="4-6">Medium Risk (4-6)</SelectItem>
                  <SelectItem value="7-10">High Risk (7-10)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Min Amount (ETH)</label>
              <Input
                data-testid="min-amount-input"
                type="number"
                step="0.1"
                placeholder="0.0"
                value={filters.minAmount}
                onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Max Amount (ETH)</label>
              <Input
                data-testid="max-amount-input"
                type="number"
                step="0.1"
                placeholder="10.0"
                value={filters.maxAmount}
                onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                className="border-slate-300 focus:border-indigo-600 focus:ring-indigo-600"
              />
            </div>
          </div>
        </div>

        <div data-testid="invoices-section">
          <div className="flex items-center justify-between mb-6">
            <p className="text-slate-600 font-medium">
              {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {loading ? (
            <div data-testid="loading-spinner" className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div data-testid="no-invoices" className="text-center py-12 text-muted-foreground">
              <p>No invoices match your filters. Try adjusting your search criteria.</p>
            </div>
          ) : (
            <div data-testid="invoices-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInvoices.map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  onFinance={handleFinance}
                  onViewDetails={handleViewDetails}
                  userType="investor"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};